/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: DeviceForm.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Devices module.
----------------------------------------------------------------*/

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { FormBox } from '../../components/ui/FormBox';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import Combobox from '../../components/ui/Combobox';
import Select from '../../components/ui/select';
import ModuleFormHeader from '../../components/layouts/ModuleFormHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, Monitor, Eye, Trash2, Copy, Plus, Image as ImageIcon } from 'lucide-react';
import Editor from '@monaco-editor/react';
import { Button } from '../../components/ui/button';
import Modal from '../../components/ui/Modal';
import ConfirmationModal from '../../components/ui/ConfirmationModal';

import * as api from './api';
import * as templatesApi from '../provisioning-templates/api';
import * as vendorsApi from '../provisioning-vendors/api';
import * as modelsApi from '../provisioning-device-models/api';
import { fetchExtensions } from '../extensions/api/extensionsApi';
import { fetchPhonebooks } from '../phonebooks/api/phonebooksApi';
import { extractDssKeysInfo, extractPhonebookInfo, extractExpansionModuleInfo } from '../provisioning-templates/utils/extractOptions';
import { ExpansionKeyConfig, DssKeyConfig, PhonebookEntryConfig } from '../provisioning-templates/types';

import { DeviceDssTab } from './tabs/DeviceDssTab';
import { DevicePhonebookTab } from './tabs/DevicePhonebookTab';
import { DeviceExpansionTab } from './tabs/DeviceExpansionTab';

interface DeviceFormData {
    macAddress: string;
    description: string;
    vendorId: string;
    modelId: string;
    templateId: string;
    ipAddress?: string;
}

export default function DeviceForm() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const isNew = !id;

    const [vendors, setVendors] = useState<vendorsApi.Vendor[]>([]);
    const [allModels, setAllModels] = useState<modelsApi.DeviceModel[]>([]);
    const [allTemplates, setAllTemplates] = useState<templatesApi.Template[]>([]);
    const [extensions, setExtensions] = useState<{ id: number | string; extension: string; name?: string; displayName?: string }[]>([]);
    const [isLoadingInitial, setIsLoadingInitial] = useState(true);
    const [selectedVendor, setSelectedVendor] = useState<vendorsApi.Vendor | null>(null);
    const [selectedModel, setSelectedModel] = useState<modelsApi.DeviceModel | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<templatesApi.Template | null>(null);
    const [deviceAccounts, setDeviceAccounts] = useState<{ line: number; extensionId?: string; extension?: string; displayName?: string }[]>([]);
    const [deviceKeys, setDeviceKeys] = useState<DssKeyConfig[]>([]);
    const [devicePhonebook, setDevicePhonebook] = useState<PhonebookEntryConfig[]>([]);
    const [availablePhonebooks, setAvailablePhonebooks] = useState<{ id?: number; uuid?: string; description: string; url?: string }[]>([]);
    const [deviceExpansionModules, setDeviceExpansionModules] = useState<ExpansionKeyConfig[]>([]);
    const [expPage, setExpPage] = useState(1);
    const [expPerPage, setExpPerPage] = useState(10);
    const [selectedExpModule, setSelectedExpModule] = useState(1);
    const [dssPage, setDssPage] = useState(1);
    const [dssPerPage, setDssPerPage] = useState(10);
    const [isKeysOverridden, setIsKeysOverridden] = useState(false);
    const [isPhonebookOverridden, setIsPhonebookOverridden] = useState(false);
    const [isExpansionModulesOverridden, setIsExpansionModulesOverridden] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewContent, setPreviewContent] = useState('');
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [copiedToClipboard, setCopiedToClipboard] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [previewImageSrc, setPreviewImageSrc] = useState<string | null>(null);

    const { control, handleSubmit, setValue, watch, getValues, reset, formState: { errors } } = useForm<DeviceFormData>({
        defaultValues: { macAddress: '', description: '', vendorId: '', modelId: '', templateId: '', ipAddress: '' }
    });

    const watchedVendorId = watch('vendorId');
    const watchedModelId = watch('modelId');
    const watchedTemplateId = watch('templateId');

    const availableModels = useMemo(() => {
        if (!watchedVendorId) return [];
        return allModels.filter(m => m.vendorId.toString() === watchedVendorId);
    }, [allModels, watchedVendorId]);

    const availableTemplates = useMemo(() => {
        if (!watchedModelId) return [];
        return allTemplates.filter(t => t.modelId.toString() === watchedModelId);
    }, [allTemplates, watchedModelId]);

    const paramMac = searchParams.get('mac');
    const paramVendor = searchParams.get('vendor');
    const paramIp = searchParams.get('ip');

    const loadData = useCallback(async () => {
        setIsLoadingInitial(true);
        try {
            const [vData, mData, tData, eData, pbData] = await Promise.all([
                vendorsApi.getVendors(), modelsApi.getDeviceModels(), templatesApi.getTemplates(),
                fetchExtensions({ page: 1, limit: 1000, sortBy: 'extension', sortOrder: 'asc' }),
                fetchPhonebooks({ page: 1, limit: 1000, sortBy: 'description', sortOrder: 'asc' })
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .then(res => Array.isArray(res.data) ? res.data : ((res.data as any).data || []))
                    .catch(() => [])
            ]);
            setVendors(vData); setAllModels(mData); setAllTemplates(tData);
            setExtensions(eData.data); setAvailablePhonebooks(pbData);

            let defaultValues: DeviceFormData & { ipAddress: string } = { macAddress: '', description: '', vendorId: '', modelId: '', templateId: '', ipAddress: '' };

            if (isNew) {
                if (paramMac) defaultValues.macAddress = paramMac;
                if (paramIp) defaultValues.ipAddress = paramIp;
                if (paramVendor) {
                    const match = vData.find((v: vendorsApi.Vendor) => v.name.toLowerCase().includes(paramVendor.toLowerCase()) || paramVendor.toLowerCase().includes(v.name.toLowerCase()));
                    if (match) defaultValues.vendorId = match.id.toString();
                }
                setDeviceAccounts([]); setDeviceKeys([]); setDevicePhonebook([]); setDeviceExpansionModules([]);
                setIsKeysOverridden(false); setIsPhonebookOverridden(false); setIsExpansionModulesOverridden(false);
            } else {
                const device = await api.getDevice(id!);
                defaultValues = { macAddress: device.macAddress, description: device.description || '', vendorId: device.vendorId.toString(), modelId: device.modelId.toString(), templateId: device.templateId.toString(), ipAddress: device.ipAddress || '' };
                if (device.accounts) setDeviceAccounts(device.accounts as { line: number; extensionId?: string; extension?: string; displayName?: string }[]);
                else setDeviceAccounts([]);

                const devModel = mData.find((m) => m.id === device.modelId);
                const devTemplate = tData.find((t) => t.id === device.templateId);

                if (device.keys && device.keys.length > 0) { setDeviceKeys(device.keys as DssKeyConfig[]); setIsKeysOverridden(true); }
                else {
                    setIsKeysOverridden(false);
                    if (devModel && devTemplate) {
                        const dssInfo = extractDssKeysInfo(devModel.keys, devModel.accounts);
                        let fullKeys: DssKeyConfig[] = [];
                        if (dssInfo.count > 0) {
                            fullKeys = Array.from({ length: dssInfo.count }, (_, i): DssKeyConfig => ({ keyNumber: i + 1, type: 'Line', description: '', value: '', extension: '', line: `Line ${Math.min(i + 1, dssInfo.maxAccounts)}` }));
                        }
                        const tplKeys = devTemplate.keys || [];
                        if (Array.isArray(tplKeys) && tplKeys.length > 0) {
                            (tplKeys as DssKeyConfig[]).forEach((k) => { const idx = k.keyNumber - 1; if (idx >= 0 && idx < fullKeys.length) fullKeys[idx] = { ...fullKeys[idx], ...k }; });
                        }
                        setDeviceKeys(fullKeys);
                    } else setDeviceKeys([]);
                }

                if (device.phonebook && device.phonebook.length > 0) { setDevicePhonebook(device.phonebook as PhonebookEntryConfig[]); setIsPhonebookOverridden(true); }
                else { setIsPhonebookOverridden(false); if (devTemplate) setDevicePhonebook((devTemplate.phonebook as PhonebookEntryConfig[]) || []); else setDevicePhonebook([]); }

                if (device.expansionModules && device.expansionModules.length > 0) { setDeviceExpansionModules(device.expansionModules as ExpansionKeyConfig[]); setIsExpansionModulesOverridden(true); }
                else {
                    setIsExpansionModulesOverridden(false);
                    if (devModel && devTemplate) {
                        const expInfo = extractExpansionModuleInfo(devModel.expansionModuleKeys, devModel.accounts);
                        let fullExpKeys: ExpansionKeyConfig[] = [];
                        if (expInfo.moduleCount > 0) {
                            const totalKeys = expInfo.moduleCount * expInfo.keysPerModule;
                            fullExpKeys = Array.from({ length: totalKeys }, (_, i) => {
                                const moduleNum = Math.floor(i / expInfo.keysPerModule) + 1;
                                const keyNum = (i % expInfo.keysPerModule) + 1;
                                return { moduleNumber: moduleNum, keyNumber: keyNum, type: 'Line', description: '', value: '', extension: '', line: `Line ${Math.min(keyNum, expInfo.maxAccounts)}` };
                            });
                        }
                        const tplExpKeys = devTemplate.expansionModules || [];
                        if (Array.isArray(tplExpKeys) && tplExpKeys.length > 0) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (tplExpKeys as ExpansionKeyConfig[]).forEach((k) => { const idx = fullExpKeys.findIndex((fek: any) => fek.moduleNumber === k.moduleNumber && fek.keyNumber === k.keyNumber); if (idx !== -1) fullExpKeys[idx] = { ...fullExpKeys[idx], ...k }; });
                        }
                        setDeviceExpansionModules(fullExpKeys);
                    } else setDeviceExpansionModules([]);
                }
            }
            reset(defaultValues);
        } catch (error) {
            console.error(error);
            showError(t('common.loadError', 'Failed to load data'));
        } finally { setIsLoadingInitial(false); }
    }, [id, isNew, paramMac, paramVendor, paramIp]);

    useEffect(() => { loadData(); }, [loadData]);

    useEffect(() => {
        if (watchedVendorId) setSelectedVendor(vendors.find(v => v.id.toString() === watchedVendorId));
        else setSelectedVendor(null);
    }, [watchedVendorId, vendors]);

    useEffect(() => {
        if (watchedModelId) setSelectedModel(allModels.find(m => m.id.toString() === watchedModelId));
        else setSelectedModel(null);
    }, [watchedModelId, allModels]);

    useEffect(() => {
        if (watchedTemplateId) setSelectedTemplate(allTemplates.find(t => t.id.toString() === watchedTemplateId));
        else setSelectedTemplate(null);
    }, [watchedTemplateId, allTemplates]);

    useEffect(() => {
        if (selectedTemplate && selectedModel) {
            if (!isKeysOverridden) {
                const dssInfo = extractDssKeysInfo(selectedModel.keys, selectedModel.accounts);
                let fullKeys: DssKeyConfig[] = [];
                if (dssInfo.count > 0) {
                    fullKeys = Array.from({ length: dssInfo.count }, (_, i): DssKeyConfig => ({ keyNumber: i + 1, type: 'Line', description: '', value: '', extension: '', line: `Line ${Math.min(i + 1, dssInfo.maxAccounts)}` }));
                }
                const tplKeys = selectedTemplate.keys || [];
                if (Array.isArray(tplKeys) && tplKeys.length > 0) {
                    (tplKeys as DssKeyConfig[]).forEach((k) => { const idx = k.keyNumber - 1; if (idx >= 0 && idx < fullKeys.length) fullKeys[idx] = { ...fullKeys[idx], ...k }; });
                }
                setDeviceKeys(fullKeys);
            }
            if (!isPhonebookOverridden) setDevicePhonebook((selectedTemplate.phonebook as PhonebookEntryConfig[]) || []);
            if (!isExpansionModulesOverridden) {
                const expInfo = extractExpansionModuleInfo(selectedModel.expansionModuleKeys, selectedModel.accounts);
                let fullExpKeys: ExpansionKeyConfig[] = [];
                if (expInfo.moduleCount > 0) {
                    const totalKeys = expInfo.moduleCount * expInfo.keysPerModule;
                    fullExpKeys = Array.from({ length: totalKeys }, (_, i): ExpansionKeyConfig => {
                        const moduleNum = Math.floor(i / expInfo.keysPerModule) + 1;
                        const keyNum = (i % expInfo.keysPerModule) + 1;
                        return { moduleNumber: moduleNum, keyNumber: keyNum, type: 'Line', description: '', value: '', extension: '', line: `Line ${Math.min(keyNum, expInfo.maxAccounts)}` };
                    });
                }
                const tplExpKeys = selectedTemplate.expansionModules || [];
                if (Array.isArray(tplExpKeys) && tplExpKeys.length > 0) {
                    (tplExpKeys as ExpansionKeyConfig[]).forEach((k) => { const idx = fullExpKeys.findIndex((fek) => fek.moduleNumber === k.moduleNumber && fek.keyNumber === k.keyNumber); if (idx !== -1) fullExpKeys[idx] = { ...fullExpKeys[idx], ...k }; });
                }
                setDeviceExpansionModules(fullExpKeys);
            }
        }
    }, [selectedTemplate, selectedModel, isKeysOverridden, isPhonebookOverridden, isExpansionModulesOverridden]);

    useEffect(() => {
        if (isExpansionModulesOverridden && selectedModel && deviceExpansionModules.length === 0) {
            const expInfo = extractExpansionModuleInfo(selectedModel.expansionModuleKeys, selectedModel.accounts);
            if (expInfo.moduleCount > 0) {
                const totalKeys = expInfo.moduleCount * expInfo.keysPerModule;
                const emptyKeys = Array.from({ length: totalKeys }, (_, i) => {
                    const moduleNum = Math.floor(i / expInfo.keysPerModule) + 1;
                    const keyNum = (i % expInfo.keysPerModule) + 1;
                    return { moduleNumber: moduleNum, keyNumber: keyNum, type: 'Line', description: '', value: '', extension: '', line: `Line ${Math.min(keyNum, expInfo.maxAccounts)}` };
                });
                setDeviceExpansionModules(emptyKeys);
            }
        }
    }, [isExpansionModulesOverridden, selectedModel]);

    useEffect(() => {
        if (selectedModel?.imageFile) {
            const rawPath = selectedModel.imageFile.replace(/\\/g, '/');
            const safePath = rawPath.split('/').map(s => encodeURIComponent(s)).join('/');
            setPreviewImageSrc(`/uploads/${safePath}`);
        } else setPreviewImageSrc(null);
    }, [selectedModel]);

    const handleImageError = () => {
        if (!previewImageSrc || !selectedModel) return;
        if (previewImageSrc.includes('%20')) {
            const newSrc = previewImageSrc.replace(/%20/g, '-');
            if (newSrc !== previewImageSrc) { setPreviewImageSrc(newSrc); return; }
        }
        setPreviewImageSrc(null);
    };

    const handleCopy = (text: string, callback?: () => void) => {
        const doCopy = () => { showSuccess(t('common.copied', 'Copied!')); if (callback) callback(); };
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text).then(doCopy).catch(() => fallbackCopy(text, doCopy));
        } else fallbackCopy(text, doCopy);
    };

    const fallbackCopy = (text: string, onSuccess: () => void) => {
        const textArea = document.createElement("textarea");
        textArea.value = text; textArea.style.position = "fixed"; textArea.style.left = "-9999px";
        document.body.appendChild(textArea); textArea.focus(); textArea.select();
        try { document.execCommand('copy'); onSuccess(); } catch (err) { console.error('Copy failed', err); }
        document.body.removeChild(textArea);
    };

    const handleSaveInternal = useCallback(async () => {
        let savedResult;
        await handleSubmit(
            async (data) => {
                const payload = { ...data, keys: isKeysOverridden ? deviceKeys : [], phonebook: isPhonebookOverridden ? devicePhonebook : [], expansionModules: isExpansionModulesOverridden ? deviceExpansionModules : [], accounts: deviceAccounts };
                try {
                    if (isNew) { savedResult = await api.createDevice(payload); showSuccess(t('provisioningDevices.createSuccess', 'Device created successfully')); }
                    else { savedResult = await api.updateDevice(id!, payload); showSuccess(t('provisioningDevices.updateSuccess', 'Device updated successfully')); }
                } catch (e) { console.error("Save error:", e); throw e; }
            },
            (errors) => { 
                console.error("Validation errors:", errors); 
                const errorMsgs = Object.values(errors)
                    .map((err: any) => err?.message)
                    .filter(msg => typeof msg === 'string')
                    .join(' � ');
                showError(`${t('common.validationError', 'Please fix the errors in the form')}${errorMsgs ? ': ' + errorMsgs : ''}`);
                throw new Error('Validation failed'); 
            }
        )();
        return savedResult;
    }, [handleSubmit, isNew, id, isKeysOverridden, deviceKeys, isPhonebookOverridden, devicePhonebook, isExpansionModulesOverridden, deviceExpansionModules, deviceAccounts, showSuccess, t]);

    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal, listPath: '/settings/provisioning/provisioning-devices',
        newPath: '/settings/provisioning/provisioning-devices/new', mode: isNew ? 'create' : 'edit',
        getEditPath: (savedData) => `/settings/provisioning/provisioning-devices/${savedData?.id || id}`,
        storageKey: 'provisioningDevices:saveAndNewData',
    });

    const handleCancel = () => { loadData(); };

    const handlePreview = async () => {
        if (!selectedModel || !selectedTemplate || !selectedVendor) { showError(t('provisioningDevices.selectRequired', 'Please select Vendor, Model and Template first')); return; }
        const values = getValues();
        setIsPreviewing(true);
        try {
            const payload = { id, macAddress: values.macAddress, description: values.description, vendorId: selectedVendor.id, modelId: selectedModel.id, templateId: selectedTemplate.id, keys: isKeysOverridden ? deviceKeys : [], expansionModules: isExpansionModulesOverridden ? deviceExpansionModules : [], phonebook: isPhonebookOverridden ? devicePhonebook : [], accounts: deviceAccounts, ipAddress: values.ipAddress };
            const res = await api.previewConfig(payload);
            setPreviewContent(res.config); setPreviewOpen(true);
        } catch (error) { console.error(error); showError(t('provisioningDevices.previewError', 'Failed to generate preview')); }
        finally { setIsPreviewing(false); }
    };

    const handleDelete = async () => {
        if (!id) return;
        setIsDeleting(true);
        try {
            await api.deleteDevice(id); showSuccess(t('provisioningDevices.deleteSuccess', 'Device deleted successfully'));
            navigate('/settings/provisioning/provisioning-devices');
        } catch (error) { console.error(error); showError(t('provisioningDevices.deleteError', 'Failed to delete device')); }
        finally { setIsDeleting(false); setShowDeleteModal(false); }
    };

    if (isLoadingInitial) {
        return (<div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>);
    }

    const provisioningPath = (() => {
        if (selectedTemplate?.domain?.uuid) return `/provisioning/${selectedTemplate.domain.uuid}/devices/`;
        if (selectedTemplate?.serverSettings?.serverDomain) {
            const safeDomain = selectedTemplate.serverSettings.serverDomain.replace(/[^a-zA-Z0-9.-]/g, '_');
            return `/provisioning/${safeDomain}/devices/`;
        }
        return '/provisioning/';
    })();
    const provisioningUrl = `${window.location.protocol}//${window.location.hostname}${provisioningPath}`;

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                title={isNew ? t('provisioningDevices.create', 'Add Device') : t('provisioningDevices.edit', 'Edit Device')}
                backButtonText={t('common.list', 'List')} onBack={() => navigate('/settings/provisioning/provisioning-devices')}
                icon={<Monitor className="h-4 w-4 text-primary" />}
                actions={[{ icon: <Plus className="h-5 w-5 text-muted-foreground" />, tooltip: t('provisioningDevices.add'), onClick: () => navigate('/settings/provisioning/provisioning-devices/new') }]}
            >
                <SimpleTooltip content={t('provisioningDevices.tooltips.preview')} position="bottom">
                    <Button variant="outline" onClick={handlePreview} disabled={isPreviewing}>
                        {isPreviewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
                        {t('common.preview', 'Preview Config')}
                    </Button>
                </SimpleTooltip>
            </ModuleFormHeader>

            <div className="mt-8">
                <Tabs defaultValue="general">
                    <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1">
                        <TabsTrigger value="general">{t('common.general', 'General')}</TabsTrigger>
                        <TabsTrigger value="accounts">{t('provisioningDevices.accounts', 'Accounts')}</TabsTrigger>
                        {selectedModel && extractDssKeysInfo(selectedModel.keys, selectedModel.accounts).count > 0 && (
                            <TabsTrigger value="dss">{t('provisioningDevices.dssKeys', 'DSS Keys')} {isKeysOverridden && '*'}</TabsTrigger>
                        )}
                        {selectedModel && extractExpansionModuleInfo(selectedModel.expansionModuleKeys).moduleCount > 0 && (
                            <TabsTrigger value="expansion">{t('provisioningDevices.expansionModules', 'Expansion')} {isExpansionModulesOverridden && '*'}</TabsTrigger>
                        )}
                        {selectedModel && (selectedModel.phonebook || (extractPhonebookInfo && extractPhonebookInfo(selectedModel.phonebook))) && (
                            <TabsTrigger value="phonebook">{t('provisioningDevices.phonebook', 'Phonebook')} {isPhonebookOverridden && '*'}</TabsTrigger>
                        )}
                    </TabsList>

                    {/* GENERAL TAB */}
                    <TabsContent value="general">
                        <FormBox title={t('common.general', 'General Information')} icon={<Monitor className="h-4 w-4" />}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div className="col-span-2 grid grid-cols-2 gap-x-4 gap-y-3 items-center">
                                    <SimpleTooltip content={t('provisioningDevices.tooltips.mac')} position="top"><Label required className="text-left">{t('provisioningDevices.mac', 'MAC Address')}</Label></SimpleTooltip>
                                    <Controller name="macAddress" control={control} rules={{ required: t('common.required', 'Required') }} render={({ field }) => (<Input required error={errors.macAddress?.message} {...field} placeholder="00:00:00:00:00:00" />)} />
                                    <SimpleTooltip content={t('provisioningDevices.tooltips.description')} position="top"><Label className="text-left">{t('common.description', 'Description')}</Label></SimpleTooltip>
                                    <Controller name="description" control={control} render={({ field }) => (<Input {...field} placeholder={t('provisioningDevices.descPlaceholder', 'Device description...')} />)} />
                                    <SimpleTooltip content={t('provisioningDevices.tooltips.vendor')} position="top"><Label required className="text-left">{t('provisioningDevices.vendor', 'Vendor')}</Label></SimpleTooltip>
                                    <Controller name="vendorId" control={control} rules={{ required: t('common.required', 'Required') }} render={({ field }) => (
                                        <Select value={field.value?.toString() || ''} onValueChange={val => { field.onChange(val); setValue('modelId', ''); setValue('templateId', ''); }}
                                            options={vendors.map(v => ({ value: v.id.toString(), label: v.name }))} placeholder={t('common.selectOne', '-- Select One --')} searchable />
                                    )} />
                                    <SimpleTooltip content={t('provisioningDevices.tooltips.model')} position="top"><Label required className="text-left">{t('provisioningDevices.phoneModel', 'Phone Model')}</Label></SimpleTooltip>
                                    <Controller name="modelId" control={control} rules={{ required: t('common.required', 'Required') }} render={({ field }) => (
                                        <Select value={field.value?.toString() || ''} onValueChange={val => { field.onChange(val); setValue('templateId', ''); }}
                                            options={availableModels.map(m => ({ value: m.id.toString(), label: m.model }))} placeholder={t('common.selectOne', '-- Select One --')} disabled={!watchedVendorId} searchable />
                                    )} />
                                    <SimpleTooltip content={t('provisioningDevices.tooltips.template')} position="top"><Label required className="text-left">{t('provisioningDevices.template', 'Template')}</Label></SimpleTooltip>
                                    <Controller name="templateId" control={control} rules={{ required: t('common.required', 'Required') }} render={({ field }) => (
                                        <Select value={field.value?.toString() || ''} onValueChange={field.onChange}
                                            options={availableTemplates.map(t => ({ value: t.id.toString(), label: t.name }))} placeholder={t('common.selectOne', '-- Select One --')} disabled={!watchedModelId} searchable />
                                    )} />
                                    <SimpleTooltip content={t('provisioningDevices.tooltips.ip')} position="top"><Label className="text-left">{t('provisioningDevices.ip', 'IP Address')}</Label></SimpleTooltip>
                                    <Controller name="ipAddress" control={control} render={({ field }) => (<Input {...field} disabled className="bg-muted/50" placeholder={t('provisioningDevices.ipAuto', 'Auto-detected')} />)} />
                                </div>
                                <div className="col-span-2 flex items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center p-6 bg-muted/10 rounded-lg border border-dashed border-border min-h-[250px]">
                                        {selectedModel ? (
                                            previewImageSrc ? (
                                                <div className="flex flex-col items-center">
                                                    <img key={previewImageSrc} src={previewImageSrc} alt={selectedModel.model || selectedModel.modelName} className="max-h-48 object-contain" onError={handleImageError} />
                                                    <div className="mt-4 text-center"><h4 className="font-semibold text-foreground text-lg">{selectedModel.model || selectedModel.modelName}</h4><p className="text-sm text-muted-foreground">{selectedVendor?.name}</p></div>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center text-muted-foreground">
                                                    <ImageIcon className="h-16 w-16 mb-3 opacity-40" /><span className="text-sm">{t('provisioningDevices.noImage', 'No image available')}</span>
                                                    <div className="mt-4 text-center"><h4 className="font-semibold text-foreground text-lg">{selectedModel.model || selectedModel.modelName}</h4><p className="text-sm text-muted-foreground">{selectedVendor?.name}</p></div>
                                                </div>
                                            )
                                        ) : (
                                            <div className="flex flex-col items-center text-muted-foreground"><Monitor className="h-16 w-16 mb-3 opacity-30" /><span className="text-sm">{t('provisioningDevices.selectModel', 'Select a model to preview')}</span></div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-center">
                                    <SimpleTooltip content={t('provisioningDevices.tooltips.url')} position="top"><Label className="text-left">{t('provisioningDevices.provisioningUrl', 'Provisioning URL')}</Label></SimpleTooltip>
                                    <div className="col-span-3 flex gap-2">
                                        <Input readOnly value={provisioningUrl} className="bg-muted/50 text-muted-foreground font-mono text-sm" />
                                        <SimpleTooltip content={t('provisioningDevices.tooltips.copyUrl')} position="top">
                                            <Button type="button" variant="outline" size="icon" onClick={() => handleCopy(provisioningUrl)}><Copy className="h-4 w-4" /></Button>
                                        </SimpleTooltip>
                                    </div>
                                </div>
                            </div>
                        </FormBox>
                    </TabsContent>

                    {/* ACCOUNTS TAB */}
                    <TabsContent value="accounts" className="space-y-6">
                        <FormBox title={t('provisioningDevices.accounts', 'SIP Accounts')}
                            description={selectedModel ? `${t('provisioningDevices.accountsDescWithCount', 'This model supports')} ${selectedModel.maxAccounts || 1} ${t('provisioningDevices.sipAccounts', 'SIP account(s)')}. ${t('provisioningDevices.configureLines', 'Configure the extensions for each line.')}` : t('provisioningDevices.accountsDesc', 'Assign accounts to device lines')}>
                            {!selectedModel ? (
                                <div className="text-center py-8 text-muted-foreground bg-muted/10 rounded-lg">{t('provisioningDevices.selectModelFirst', 'Please select a phone model first to configure SIP accounts.')}</div>
                            ) : (
                                <div className="space-y-2">
                                    {Array.from({ length: selectedModel.maxAccounts || 1 }, (_, idx) => {
                                        const lineNumber = idx + 1;
                                        const existingAccount = deviceAccounts.find(a => a.line === lineNumber);
                                        return (
                                            <div key={lineNumber} className="grid grid-cols-[100px_1fr_auto] gap-3 items-center">
                                                <Label className="text-right">{t('provisioningDevices.line', 'Line')} {lineNumber}</Label>
                                                <Combobox placeholder={t('provisioningDevices.selectExtension', 'Select Extension')} options={extensions.map(e => ({ value: String(e.id), label: `${e.extension} - ${e.name || ''}`, checked: existingAccount?.extensionId === String(e.id) }))}
                                                    onToggle={val => { setDeviceAccounts(prev => { const filtered = prev.filter(a => a.line !== lineNumber); return val ? [...filtered, { line: lineNumber, extensionId: val }] : filtered; }); }} single className="w-full" />
                                                {existingAccount ? (
                                                    <button type="button" onClick={() => setDeviceAccounts(prev => prev.filter(a => a.line !== lineNumber))} className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors" title={t('common.clear', 'Clear')}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                ) : <div className="w-8" />}
                                            </div>
                                        );
                                    })}
                                    <div className="text-xs text-muted-foreground pt-2 text-right">{deviceAccounts.length} {t('provisioningDevices.of', 'of')} {selectedModel.maxAccounts || 1} {t('provisioningDevices.accountsConfigured', 'accounts configured')}</div>
                                </div>
                            )}
                        </FormBox>
                    </TabsContent>

                    {/* DSS KEYS TAB */}
                    {selectedModel && extractDssKeysInfo(selectedModel.keys, selectedModel.accounts).count > 0 && (
                        <TabsContent value="dss" className="space-y-6">
                            <DeviceDssTab selectedModel={selectedModel} selectedTemplate={selectedTemplate} deviceKeys={deviceKeys} setDeviceKeys={setDeviceKeys}
                                isKeysOverridden={isKeysOverridden} setIsKeysOverridden={setIsKeysOverridden}
                                dssPage={dssPage} setDssPage={setDssPage} dssPerPage={dssPerPage} setDssPerPage={setDssPerPage} />
                        </TabsContent>
                    )}

                    {/* PHONEBOOK TAB */}
                    {selectedModel && (selectedModel.phonebook || (extractPhonebookInfo && extractPhonebookInfo(selectedModel.phonebook))) && (
                        <TabsContent value="phonebook" className="space-y-6">
                            <DevicePhonebookTab selectedModel={selectedModel} selectedTemplate={selectedTemplate} devicePhonebook={devicePhonebook} setDevicePhonebook={setDevicePhonebook}
                                isPhonebookOverridden={isPhonebookOverridden} setIsPhonebookOverridden={setIsPhonebookOverridden} availablePhonebooks={availablePhonebooks} />
                        </TabsContent>
                    )}

                    {/* EXPANSION MODULES TAB */}
                    {selectedModel && extractExpansionModuleInfo(selectedModel.expansionModuleKeys).moduleCount > 0 && (
                        <TabsContent value="expansion" className="space-y-6">
                            <DeviceExpansionTab selectedModel={selectedModel} selectedTemplate={selectedTemplate} deviceExpansionModules={deviceExpansionModules} setDeviceExpansionModules={setDeviceExpansionModules}
                                isExpansionModulesOverridden={isExpansionModulesOverridden} setIsExpansionModulesOverridden={setIsExpansionModulesOverridden}
                                expPage={expPage} setExpPage={setExpPage} expPerPage={expPerPage} setExpPerPage={setExpPerPage}
                                selectedExpModule={selectedExpModule} setSelectedExpModule={setSelectedExpModule} />
                        </TabsContent>
                    )}
                </Tabs>
            </div>

            <FixedActionBar onSaveWithAction={handleSaveWithAction} onCancel={handleCancel}
                onDelete={!isNew ? () => setShowDeleteModal(true) : undefined} isEditMode={!isNew} isSaving={false}
                deleteDisabled={isDeleting} cancelText={t('common.cancel')} deleteText={t('common.delete')} useSaveDropdown={true} saveDropdownStorageKey="provisioningDevices:saveAction" />

            <ConfirmationModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} onConfirm={handleDelete}
                title={t('provisioningDevices.deleteTitle', 'Delete Device')} message={t('provisioningDevices.deleteConfirm', 'Are you sure you want to delete this device?')}
                confirmText={t('common.delete')} cancelText={t('common.cancel')} type="danger" isLoading={isDeleting} />

            {/* Config Preview Modal */}
            <Modal isOpen={previewOpen} onClose={() => setPreviewOpen(false)} title={t('provisioningDevices.preview', 'Configuration Preview')} size="lg">
                <div className="flex gap-2 mb-4">
                    <Button variant="outline" size="sm" onClick={() => handleCopy(previewContent, () => setCopiedToClipboard(true))}>
                        <Copy className="h-4 w-4 mr-2" />{copiedToClipboard ? t('common.copied', 'Copied!') : t('common.copy', 'Copy')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => {
                        const blob = new Blob([previewContent], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = 'device-config.cfg'; a.click(); URL.revokeObjectURL(url);
                    }}>
                        <Eye className="h-4 w-4 mr-2" />{t('common.download', 'Download')}
                    </Button>
                </div>
                <Editor height="60vh" defaultLanguage="xml" value={previewContent} options={{ readOnly: true, minimap: { enabled: false }, wordWrap: 'on' }} theme="vs-dark" />
            </Modal>
        </div>
    );
}
